import { useEffect, useState } from 'react';
import { ApiError } from '../lib/api';
import Eyebrow from './Eyebrow';
import Button from './Button';

interface Props {
  eyebrow: string;
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  busyLabel: string;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
}

/**
 * Generic destructive-action confirm dialog — same chrome as the script
 * DeleteConfirmModal in AdminDashboard.tsx, generalised for reuse across the
 * bundle ("Links") delete flows (dashboard row + editor page).
 */
export default function ConfirmModal({
  eyebrow,
  title,
  body,
  confirmLabel,
  busyLabel,
  onCancel,
  onConfirm,
}: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm() {
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
      setBusy(false);
    }
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !busy) onCancel();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  return (
    <div
      className="fixed inset-0 bg-ink-900/70 backdrop-blur-md flex items-center justify-center px-6 z-50 animate-fade-in"
      onClick={busy ? undefined : onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-card border border-line rounded-md p-8 shadow-flare-lg/30 animate-scale-in"
      >
        <Eyebrow accent>// {eyebrow}</Eyebrow>
        <h2 className="font-display font-bold text-[24px] leading-[1.15] tracking-[-0.01em] mt-3 mb-2">
          {title}
        </h2>
        <div className="text-faint text-[13px] leading-[1.55] mb-7">{body}</div>

        {error && <p className="text-accent text-[13px] mb-4">{error}</p>}

        <div className="flex flex-col md:flex-row md:justify-end gap-3">
          <Button type="button" variant="ghost" className="w-full md:w-auto" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button type="button" variant="primary" className="w-full md:w-auto" onClick={handleConfirm} disabled={busy}>
            {busy ? busyLabel : confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
